import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  IconButton, Chip, CircularProgress, TablePagination, Tooltip
} from '@mui/material';
import DoneIcon from '@mui/icons-material/Done';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import CloseIcon from '@mui/icons-material/Close';

const NotificationList = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const token = JSON.parse(localStorage.getItem('auth_token_crm'))?.token;
    setLoading(true);
    const url = `${process.env.REACT_APP_SERVER_URI}/notifications/list?page=${page + 1}&limit=${rowsPerPage}`;
    fetch(url, {
      headers: { Authorization: token ? `Bearer ${token}` : '' },
    })
      .then(res => res.json())
      .then(data => {
        const list = Array.isArray(data?.notifications) ? data.notifications : (Array.isArray(data) ? data : (data?.notifications || []));
        setNotifications(list);
        setTotal(data?.total ?? list.length);
        setLoading(false);
      })
      .catch(() => {
        setNotifications([]);
        setTotal(0);
        setLoading(false);
      });
  }, [page, rowsPerPage]);

  const markAsRead = async (id) => {
    const token = JSON.parse(localStorage.getItem('auth_token_crm'))?.token;
    try {
      await fetch(`${process.env.REACT_APP_SERVER_URI}/notifications/mark-read/${id}`, {
        method: 'POST',
        headers: { Authorization: token ? `Bearer ${token}` : '', 'Content-Type': 'application/json' },
      });
      setNotifications(prev => prev.map(n => n._id === id ? { ...n, read: true } : n));
    } catch (err) {
      // ignore
    }
  };

  const deleteNotification = async (id) => {
    const token = JSON.parse(localStorage.getItem('auth_token_crm'))?.token;
    try {
      await fetch(`${process.env.REACT_APP_SERVER_URI}/notifications/delete/${id}`, {
        method: 'DELETE',
        headers: { Authorization: token ? `Bearer ${token}` : '' },
      });
      setNotifications(prev => prev.filter(n => n._id !== id));
      setTotal(t => Math.max(0, t - 1));
    } catch (err) {
      alert('Failed to delete notification.');
    }
  };

  const handleRowClick = async (notif) => {
    if (!notif) return;
    if (!notif.read) await markAsRead(notif._id);

    if (notif.doc && notif.doc.collection && notif.doc.id) {
      if (String(notif.doc.collection).toLowerCase() === 'subscriptions') {
        navigate(`/washes/${notif.doc.id}`);
        return;
      }
      // other collections can be handled here
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>Notifications</Typography>
      {notifications.length === 0 ? (
        <Typography color="text.secondary">No notifications found.</Typography>
      ) : (
        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Message</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Doc (collection:id)</TableCell>
                  <TableCell>Created At</TableCell>
                  <TableCell>Read</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {notifications.map(n => (
                  <TableRow
                    key={n._id}
                    hover
                    onClick={() => handleRowClick(n)}
                    sx={{ cursor: 'pointer', bgcolor: n.read ? 'transparent' : 'rgba(25,118,210,0.03)' }}
                  >
                    <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {n.title || '-'}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {n.message || '-'}
                    </TableCell>
                    <TableCell>
                      <Chip label={n.type || 'info'} size="small" />
                    </TableCell>
                    <TableCell>
                      {n.doc?.collection ? `${n.doc.collection}:${n.doc?.id || '-'}` : '-'}
                    </TableCell>
                    <TableCell>
                      {n.createdAt ? new Date(n.createdAt).toLocaleString() : (n.updatedAt ? new Date(n.updatedAt).toLocaleString() : '-')}
                    </TableCell>
                    <TableCell>{n.read ? 'Yes' : 'No'}</TableCell>
                    <TableCell align="right">
                      <Tooltip title={n.read ? 'Mark unread' : 'Mark read'}>
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); markAsRead(n._id); }}
                        >
                          {n.read ? <DoneIcon fontSize="small" /> : <MarkEmailReadIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Close notification">
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); deleteNotification(n._id); }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box display="flex" justifyContent="center">
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default NotificationList;
